import {
  ACCESSIBILITY_ARGS,
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { ROLE_OPTION } from '../../../../utils/spds/constants/aria';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import Button from '../button.gjs';
import Super from '../base.gjs';
import styles from './item.module.scss';

const METADATA = Metadata.define({
  description: 'Menu item component used within menus.',
  extends: [
    pick(ACCESSIBILITY_ARGS, [{ name: 'role', default: ROLE_OPTION }]),
    pick(ACTION_ARGS, [
      {
        name: 'onClick',
        description: 'Action to be called when the menu item is clicked.',
        required: true,
      },
    ]),
    pick(COMPONENT_STATE_ARGS, ['disabled', 'hidden']),
  ],
  meta: {
    retainSelection: Arg.boolean({
      description: 'Indicates whether item will remain selected after second click',
    }),
    showSelectedStyles: Arg.boolean({
      default: true,
      description:
        'Determines whether to apply styles for selected state when the item is selected',
    }),
    value: Arg.string({ description: 'The value associated with the menu item.' }),
    wrappedContent: Arg.boolean({
      description: 'If true, wraps the yielded content in a <span> for additional styling control.',
    }),
  },
  name: 'Menu Item',
});

export default class SpdsCoreMenuItem extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu-item';
  classesToMapToArgs = ['hidden'];
  styles = styles;

  @tracked isSelected = 'false';

  @reads('baseElement.ariaSelected') ariaSelected;

  get keepSelectedState() {
    return this.ariaSelected === 'true' && this.args.retainSelection;
  }

  get showSelectedStyles() {
    return this.argOrDefault('showSelectedStyles');
  }

  get role() {
    return this.argOrDefault('role');
  }

  @action
  onClick() {
    if (this.showSelectedStyles && !this.keepSelectedState) {
      this.isSelected = `${this.ariaSelected === 'false'}`;
    }

    this.args.onClick(...arguments);
  }

  <template>
    <Button
      @flex="1"
      @gap="0"
      aria-current={{this.isSelected}}
      aria-selected={{this.isSelected}}
      class={{this.classes}}
      data-test-spds-menu-item
      data-value={{@value}}
      id={{this.id}}
      role={{this.role}}
      type="button"
      ...attributes
      {{this.setupComponent}}
      {{this.validateArguments}}
      {{on "click" this.onClick}}
    >
      {{#if @wrappedContent}}
        <span>{{yield}}</span>
      {{else}}
        {{yield}}
      {{/if}}
    </Button>
  </template>
}

export const SPDS_CORE_MENU_ITEM_METADATA = SpdsCoreMenuItem.metadata;
